import { Box, Dialog, DialogTitle, DialogContent, Typography, Button, DialogActions, Tab, Tabs, List, ListItem } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { CloseDialogAction } from '../../../components/shared/dialogs/CloseDialogAction';
import DragDropFileUpload from '../../../components/shared/file-upload/DragDropFileUpload';
import LoadingWheel from '../../../components/ui/LoadingWheel';
import { useContext, useState } from 'react';
import { DeliveryScheduleModel } from '../../../app/services/api/orderManagementClient';
import ValidationResultTable from '../components/ValidationResultTable';
import { UserContext } from '../../../components/shared/useUser';
import { getDisplayStoreNumberByInternal } from '../../../utils/storeNumber';
interface ValidationResult {
  updated: DeliveryScheduleModel[];
  created: DeliveryScheduleModel[];
  deleted: DeliveryScheduleModel[];
  invalid: DeliveryScheduleModel[];
}
export interface ODSUploadModalProps {
  open: boolean;
  onClose: () => void;
  onFileSelected?: (file: File) => Promise<{ success: boolean; data?: ValidationResult; message?: string }>;
  onConfirmUpload?: () => Promise<{ success: boolean; message?: string }>;
  title?: string;
  uploadLabel?: string;
  uploadSublabel?: string;
}

function ODSUploadModal(props: ODSUploadModalProps) {
  const { t } = useTranslation('odsmanagement');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isConfirming, setIsConfirming] = useState<boolean>(false);
  const [uploadResult, setUploadResult] = useState<{ success: boolean; message?: string } | null>(null);
  const [validationResult, setValidationResult] = useState<ValidationResult | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [currentStep, setCurrentStep] = useState<'upload' | 'validation' | 'complete'>('upload');
  const [activeTab, setActiveTab] = useState(0);
  const { stores } = useContext(UserContext);
  
  const handleFileSelected = async (file: File) => {
    setSelectedFile(file);
    setUploadResult(null);
    setValidationResult(null);
    setIsLoading(true);
    setCurrentStep('upload');

    try {
      const result = await props.onFileSelected?.(file);
      if (result?.success && result.data) {
        setValidationResult(result.data);
        if (result.data.invalid && result.data.invalid.length > 0) {
          setCurrentStep('upload');
          setUploadResult({ success: false, message: result.message || t('invalidItemsFound') });
        } else {
          setCurrentStep('validation');
          setUploadResult({ success: true, message: result.message });
        }
      } else {
        setUploadResult({ success: false, message: result?.message || t('uploadFailed') });
      }
    } catch (error) {
      setUploadResult({ success: false, message: t('uploadFailed') + ': ' + error });
    } finally {
      setIsLoading(false);
    }
  };

  const handleConfirmUpload = async () => {
    if (!validationResult) return;
    
    setIsConfirming(true);
    try {
      const result = await props.onConfirmUpload?.();

      if (result?.success) {
        setCurrentStep('complete');
        handleClose();
        setUploadResult({ success: true, message: result.message || t('uploadCompletedSuccessfully') });
      } else {
        setUploadResult({ success: false, message: result?.message || t('uploadFailed') });
      }
    } catch (error) {
      setUploadResult({ success: false, message: t('uploadFailed') + ': ' + error });
    } finally {
      setIsConfirming(false);
    }
  };

  const handleClose = () => {
    setSelectedFile(null);
    setUploadResult(null);
    setValidationResult(null);
    setIsLoading(false);
    setIsConfirming(false);
    setCurrentStep('upload');
    setActiveTab(0);
    props.onClose();
  };

  const getTotalChanges = () => {
    if (!validationResult) return 0;
    return validationResult.updated.length + validationResult.created.length + validationResult.deleted.length;
  };

  const hasInvalidItems = validationResult && validationResult.invalid && validationResult.invalid.length > 0;

  return (
    <Dialog
      maxWidth={'md'}
      fullWidth
      onClose={handleClose}
      aria-labelledby="ods-upload-dialog"
      open={props.open}
    >
      <DialogTitle>
        {props.title || t('uploadODSFile')}
        <CloseDialogAction onCancel={handleClose} />
      </DialogTitle>
      <DialogContent sx={{ p: 0, pb: 3, display: 'flex', flexDirection: 'column' }}>
        <Box sx={{
          py: 4,
          px: '1em',
          width: '100%',
          textAlign: 'center',
          height: '100%',
          position: 'relative',
          minHeight: currentStep === 'validation' ? '500px' : '200px',
        }}> 
          {currentStep === 'upload' && (
            <>
              <DragDropFileUpload
                acceptedFileTypes={['.xlsx', '.xls', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', 'application/vnd.ms-excel']}
                onFileSelected={handleFileSelected}
                maxFileSizeMB={10}
                label={props.uploadLabel || t('dragDropODSFileHere')}
                sublabel={props.uploadSublabel || t('orClickToBrowse')}
              />

              {hasInvalidItems && (
                <Box sx={{ mt: 2, py: 5, px: '1em' }}>
                  <Typography color="error" 
                    align="left">
                    {t('errormsg')}
                  </Typography>
                  <List sx={{ color: 'error.main', mt: 1, pl: 6, listStyleType: 'disc' }}>
                    {validationResult.invalid?.map((item, index) => (
                      <ListItem key={index}
                        sx={{ fontSize: '14px', display: 'list-item', padding: 0, pl: 0.5 }}>
                        { `Row: ${item.rowNumber}, Store: ${getDisplayStoreNumberByInternal(stores, item.storeNumber)} ${item.storeName}, Order Type: ${item.orderType}, Cutoff Date: ${item.cutOffDate}, Delivery Date: ${item.deliveryDate}` }
                      </ListItem>
                    ))}
                  </List>
                </Box>
              )}

              {uploadResult && !uploadResult.success && !hasInvalidItems && (
                <Box sx={{ textAlign: 'center', py: 2, mt: 2 }}>
                  <Typography sx={{ mb: 2, color: 'error.main', fontWeight: 500 }}>
                    ❌ {t('uploadFailed')}
                  </Typography>
                  <Typography color="error.main" 
                    sx={{ mb: 2 }}>
                    {uploadResult.message || t('fileUploadFailed')}
                  </Typography>
                  
                  {selectedFile && (
                    <Typography color="text.secondary" 
                      sx={{ mt: 2 }}>
                      📄 {selectedFile.name}
                    </Typography>
                  )}
                </Box>
              )}
            </>
          )}

          {currentStep === 'validation' && validationResult && (
            <Box sx={{ textAlign: 'left', width: '100%' }}>
              
              <Tabs value={activeTab} 
                onChange={(_, newValue) => setActiveTab(newValue)} 
                sx={{ mb: 2 }}>
                <Tab 
                  label={`${t('updated')} (${validationResult.updated.length})`} 
                  disabled={validationResult.updated.length === 0}
                />
                <Tab 
                  label={`${t('created')} (${validationResult.created.length})`} 
                  disabled={validationResult.created.length === 0}
                />
                <Tab 
                  label={`${t('deleted')} (${validationResult.deleted.length})`} 
                  disabled={validationResult.deleted.length === 0}
                />
              </Tabs>

              <Box sx={{ overflow: 'auto' }}>
                {activeTab === 0 && (
                  <ValidationResultTable 
                    data={validationResult.updated} 
                    type="updated"
                  />
                )}
                {activeTab === 1 && (
                  <ValidationResultTable 
                    data={validationResult.created} 
                    type="created"
                  />
                )}
                {activeTab === 2 && (
                  <ValidationResultTable 
                    data={validationResult.deleted} 
                    type="deleted"
                  />
                )}
              </Box>
            </Box>
          )}

          {(isLoading || isConfirming) && (
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: 'rgba(255, 255, 255, 0.9)',
                zIndex: 1,
              }}
            >
              <LoadingWheel />
              <Typography sx={{ mt: 2 }}>
                {isLoading ? t('validatingFile') : t('uploadingChanges')}
              </Typography>
            </Box>
          )}
        </Box>
      </DialogContent>
      
      <DialogActions>
        <Button 
          variant={'secondary'}
          onClick={handleClose}
          size='lg'
          disabled={isLoading || isConfirming}>
          {t('cancel')}
        </Button>
        
        {currentStep === 'validation' && validationResult && getTotalChanges() > 0 && (
          <Button 
            variant='primary'
            onClick={handleConfirmUpload}
            size='lg'
            disabled={isConfirming}>
            {t('applyChanges')}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

export default ODSUploadModal;